/* Creating a service or an asset.

   A screen rather than a dialog, and written against `kind` the same way the
   portfolio table is, so there is one create form and not two that drift.

   It asks for the six fields that make an object *findable* -- name, what it
   is, where it is in its life, how much it matters, whose it is -- and nothing
   else. The maturity scores and the seven elements of the strategic concept
   are deliberately absent: a thing being created has not been assessed, and
   seven score boxes on a create form either collect invented numbers or sit
   empty and make the form look unfinished. Saving goes straight to the
   object's own page, where all of that lives with room to explain itself.

   Unlike the detail page, which saves on blur, this one has a Save button.
   Blur-to-save needs a row to attach the change to, and there is no row until
   the first save; it would also mean a half-typed name becoming a service the
   moment somebody tabbed to the next field. */
import { useState } from "react";

import { api, plural, type Kind } from "../api";
import { usePermissions } from "../auth";
import { Field, Panel } from "../components/ui";
import { go, href } from "../route";
import { useStore } from "../store";

export default function ObjectNew({ kind }: { kind: Kind }) {
  const { meta, people, run } = useStore();
  const { canWrite } = usePermissions();

  const isService = kind === "service";
  const noun = isService ? "service" : "asset";
  const classes = (isService ? meta?.service_classes : meta?.asset_classes) ?? [];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [klass, setKlass] = useState("");
  /* A new thing starts at the beginning and in the middle: Idea is where the
     lifecycle opens, and Medium is the priority that claims nothing. Both are
     one click from anything else. */
  const [stage, setStage] = useState("Idea");
  const [priority, setPriority] = useState("Medium");
  const [owner, setOwner] = useState("");
  const [saving, setSaving] = useState(false);

  if (!meta) return <p className="note">Loading…</p>;
  if (!canWrite) {
    return (
      <p className="note">
        Your access is read-only, so you cannot create a {noun}. Ask the VP of Business Development.
      </p>
    );
  }

  const chosenClass = klass || classes[0] || "";
  const trimmed = name.trim();

  const save = async () => {
    setSaving(true);
    const created = await run(() =>
      api.create(plural(kind), {
        name: trimmed,
        description: description.trim(),
        class: chosenClass,
        lifecycle_stage: stage,
        priority,
        owner_id: owner === "" ? null : Number(owner),
      }),
    );
    setSaving(false);
    /* `run` returns undefined and shows the reason in the banner when the
       server refuses -- a name already taken, most often. Staying on the form
       with everything still typed is the whole point of checking. */
    if (created) go(href.object(kind, created.id));
  };

  return (
    <>
      <p className="crumbs">
        <a className="crumb" href={href.list(kind)}>
          {isService ? "Services" : "Assets"}
        </a>{" "}
        / New {noun}
      </p>

      <div className="detail">
        <div>
          <Panel title={`New ${noun}`}>
            <Field label="Name">
              <input
                type="text"
                autoFocus
                value={name}
                placeholder={isService ? "Harness Engineering" : "AI Engineering Pod"}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && trimmed && !saving) void save();
                }}
              />
            </Field>
            <Field label="Description">
              <textarea
                rows={10}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <div className="row">
              <Field label="Class">
                <select value={chosenClass} onChange={(e) => setKlass(e.target.value)}>
                  {classes.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Lifecycle stage">
                <select value={stage} onChange={(e) => setStage(e.target.value)}>
                  {meta.lifecycle_stages.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Priority">
                <select value={priority} onChange={(e) => setPriority(e.target.value)}>
                  {meta.priorities.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </Field>
              <Field label="Owner">
                <select value={owner} onChange={(e) => setOwner(e.target.value)}>
                  <option value="">Unassigned</option>
                  {people.map((p) => (
                    <option key={p.person_id} value={p.person_id}>
                      {p.display_name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="row" style={{ marginTop: 16 }}>
              <button
                className="btn primary"
                disabled={!trimmed || saving}
                onClick={() => void save()}
              >
                {saving ? "Creating…" : `Create ${noun}`}
              </button>
              <button className="btn" onClick={() => go(href.list(kind))}>
                Cancel
              </button>
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
